/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Period } from '@src/app/model/period';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { DateHelper } from '@src/app/shared/dateHelper';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { ValidationErrors } from '@angular/forms';
import { FormError } from '@src/app/shared/form/form-error/form-error.component';

@Injectable({
    providedIn: 'root'
})
export class PeriodService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);


    protected get baseUrl() {
        return this.getAjaxPath('budgets/periods');
    }
    protected cache: Period[];
    protected typeString = 'Periods';

    getPeriod(dateStr: string, periods: Period[]) : Period{
        /*
        if (!this.hasData) {
            const msg = $localize `Cannot getPeriod before period service retrieves data`
            this.messageService.showErrorMessage(msg);
            return null;
        }
        */
        for (const period of periods) {
            if (DateHelper.isoDateIn(dateStr, period.from, period.to)) {
                return period;
            }
        }
        const msg = $localize`Period not found for date ${dateStr}`;
        this.messageService.showErrorMessage(msg);
        return null
    }

    getOpenPeriod(dateStr: string, periods: Period[]): Period {
        const prd = this.getPeriod(dateStr, periods);
        if (!prd) {
            return null;
        }
        if (prd.statusId === Period.STATUS.OPEN.id) {
            return prd;
        } else {
            const msg = $localize`Period not open for date ${dateStr}`;
            this.messageService.showErrorMessage(msg);
            return null;
        }
    }

    block(period: Period): Observable<Period> {
        period.statusId = Period.STATUS.CLOSED.id;
        return this.put(period).pipe(first());
    }

    unblock(period: Period): Observable<Period> {
        period.statusId = Period.STATUS.OPEN.id;
        return this.put(period).pipe(first());
    }

    lock(period: Period): Observable<Period> {
        period.statusId = Period.STATUS.LOCKED.id;
        return this.put(period).pipe(first());
    }

    validateTxnDate = (periods: Period[]) => (control: AppFormControl): ValidationErrors => {
        const txnDate = control.value;
        if (!txnDate) return null; // No date to validate
        if (!DateHelper.isValidISODate(txnDate)) {
            return FormError.reportError('invalidDate',
                $localize`Not a valid date`);
        }
        if (periods.length === 0) {
            console.error('I do not have any periods', periods);
        }
        const period = periods.find( p => p.from <= txnDate && p.to >= txnDate);
        if (!period) {
            return FormError.reportError('invalidDatePeriod',
                $localize`There is no period defined for date`);
        }
        if (period.statusId !== Period.STATUS.OPEN.id) {
            return FormError.reportError('periodClosed',
                $localize`The period for this date is closed`);
        }        
    }
}
